// Fire-and-forget client -> server error logging.
// POSTs to the server's /api/clientLog so client errors land in CloudWatch
// alongside server logs. Must never throw and never block the UI.
import axios from "axios";

// Client-side dedupe: a repeating error (e.g. something throwing on an interval or animation
// frame) would otherwise POST on every occurrence and flood the endpoint. Suppress identical
// type+message pairs seen within this window. The server rate limit is keyed on a spoofable
// X-Forwarded-For, so it isn't a reliable backstop.
const DEDUPE_WINDOW_MS = 10000;
const lastSent = new Map();

function shouldSuppress(key, now) {
  // Prune stale entries so the map can't grow without bound over a long session.
  for (const [k, ts] of lastSent) {
    if (now - ts > DEDUPE_WINDOW_MS) lastSent.delete(k);
  }
  const prev = lastSent.get(key);
  if (prev !== undefined && now - prev < DEDUPE_WINDOW_MS) {
    return true;
  }
  lastSent.set(key, now);
  return false;
}

function getUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    try {
      const parsed = JSON.parse(raw);
      return (
        parsed?.email ||
        parsed?.name ||
        parsed?.userName ||
        (typeof parsed === "string" ? parsed : "")
      );
    } catch {
      return raw; // stored as a plain string
    }
  } catch {
    return "";
  }
}

export function reportClientError({ type, message, detail, level = "error" } = {}) {
  try {
    // If the server URL isn't configured we'd POST to `undefined/api/clientLog`; skip instead.
    const server = process.env.REACT_APP_BIGHORN_SERVER;
    if (!server) return;

    const safeType = type || "client-error";
    const safeMessage =
      typeof message === "string" ? message : String(message ?? "");

    // Drop identical errors seen within the dedupe window to avoid self-inflicted flooding.
    if (shouldSuppress(`${safeType}|${safeMessage}`, Date.now())) return;

    let safeDetail = detail;
    if (safeDetail && typeof safeDetail !== "string") {
      try {
        const asString = JSON.stringify(safeDetail);
        safeDetail =
          asString.length > 4000 ? asString.slice(0, 4000) : safeDetail;
      } catch {
        safeDetail = String(safeDetail);
      }
    }

    const payload = {
      level,
      type: safeType,
      message: safeMessage,
      detail: safeDetail,
      url: typeof window !== "undefined" ? window.location.href : "",
      user: getUser(),
    };

    const headers = {};
    const token = localStorage.getItem("clientToken");
    if (token) headers.ClientToken = token;

    axios
      .post(`${server}/api/clientLog`, payload, {
        headers,
      })
      .catch(() => {
        /* logging must never surface its own error */
      });
  } catch (e) {
    /* never let logging break the app */
  }
}
